import React, { ComponentProps, memo, useCallback, useRef, useState } from 'react'
import { Sheet } from 'react-modal-sheet'
import { ShortcutTooltip } from '@components/Shortcuts/ShortcutTooltip'
import { Box, IconButton, useZLayerContext } from '@ui'
import { useShortcut } from 'hooks/useShortcut'
import { MotionIconButton } from 'ui/components/Motion/MotionComponents'
import { CardOpener } from 'ui/components/Overlay/CardOpener'
import { modalSheetClass } from 'ui/styles/globals/sheet.css'

import { GiphyPicker, GiphyPickerCard } from './GiphyPicker'
import { GiphySearchContextProvider, useGiphySearchContext } from './GiphySearchContext'

type Props = ComponentProps<typeof GiphyPicker> & { parentFocused?: boolean }

// TODO: not sure why this causes whole Timeline to rerender
// const LazyGiphy = React.lazy(() => import('./GiphyPicker'))

export const GiphySheet = memo((props: Props & { showButton: boolean }) => {
    const { setIsFetching, setOptedIn, setInputValue } = useGiphySearchContext()
    const [sheetVisible, setSheetVisible] = useState(false)
    const mountPoint = useZLayerContext().rootLayerRef?.current ?? undefined

    const onClick = useCallback(() => {
        setIsFetching(true)
        setSheetVisible(true)
        setOptedIn(true)
    }, [setIsFetching, setOptedIn, setSheetVisible])

    const onCardClose = useCallback(() => {
        setInputValue('')
        setSheetVisible(false)
    }, [setInputValue, setSheetVisible])

    return (
        <>
            {props.showButton && (
                <MotionIconButton
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    icon="gif"
                    alignSelf="start"
                    onClick={onClick}
                />
            )}

            <Sheet
                className={modalSheetClass}
                mountPoint={mountPoint}
                isOpen={sheetVisible}
                onClose={onCardClose}
            >
                <Sheet.Container>
                    <Sheet.Header />
                    <Sheet.Content>
                        <Sheet.Scroller>
                            <Box
                                maxHeight="100svh"
                                overflow="auto"
                                paddingBottom="safeAreaInsetBottom"
                            >
                                <GiphyPickerCard {...props} closeCard={onCardClose} />
                            </Box>
                        </Sheet.Scroller>
                    </Sheet.Content>
                </Sheet.Container>
                <Sheet.Backdrop onTap={onCardClose} />
            </Sheet>
        </>
    )
})
GiphySheet.displayName = 'GiphySheet'

const GiphyCardOpener = memo((props: Props) => {
    const { parentFocused } = props
    const { setIsFetching, setOptedIn, setInputValue } = useGiphySearchContext()

    const onClick = useCallback(
        (
            e: React.MouseEvent,
            clickCb: ((e: React.MouseEvent<Element, MouseEvent>) => void) | undefined,
        ) => {
            setIsFetching(true)
            setOptedIn(true)
            clickCb?.(e)
        },
        [setIsFetching, setOptedIn],
    )

    const onCardClose = useCallback(() => {
        setInputValue('')
    }, [setInputValue])

    const toggleCardRef = useRef<(() => void) | undefined>(undefined)

    useShortcut(
        'OpenGifPicker',
        () => {
            toggleCardRef.current?.()
        },
        { enabled: parentFocused },
        [parentFocused],
    )

    return (
        <CardOpener
            toggleRef={toggleCardRef}
            placement="vertical"
            render={<GiphyPicker {...props} />}
            onClose={onCardClose}
        >
            {({ triggerProps: { onClick: clickCb, ...rest } }) => (
                <Box
                    tooltip={<ShortcutTooltip action="OpenGifPicker" />}
                    tooltipOptions={{
                        placement: 'vertical',
                        immediate: true,
                        removeOnClick: true,
                    }}
                    data-testid="giphy-button"
                >
                    <IconButton
                        icon="gif"
                        size="square_sm"
                        onClick={(e) => onClick(e, clickCb)}
                        {...rest}
                    />
                </Box>
            )}
        </CardOpener>
    )
})
GiphyCardOpener.displayName = 'GiphyCardOpener'

export const GiphyEntryDesktop = memo((props: Props) => {
    return (
        <GiphySearchContextProvider>
            <GiphyCardOpener {...props} />
        </GiphySearchContextProvider>
    )
})
GiphyEntryDesktop.displayName = 'GiphyEntryDesktop'

export const GiphyEntryTouch = memo((props: Props & { showButton: boolean }) => {
    return (
        <GiphySearchContextProvider>
            <GiphySheet {...props} />
        </GiphySearchContextProvider>
    )
})
GiphyEntryTouch.displayName = 'GiphyEntryTouch'
